import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import DataTable, { DataTableColumn, DataTableRow } from "../../Widgets/DataTable";
import { MedicationsFormValues } from "./interface";
import MedicationsModal, { MedicationModalSaveValues } from "./MedicationsModal";
import RowOptions from "../../Widgets/RowOptions";
import { useGetClientById } from "hooks/api/useGetClientById";
import { useUpdateClient } from "hooks/api/client/useUpdateClient";
import useGetMedications from "hooks/api/client/useGetMedications";
import useCreateMedication from "hooks/api/client/useCreateMedication";
import useUpdateMedication from "hooks/api/client/useUpdateMedication";
import useDeleteMedication from "hooks/api/client/useDeleteMedication";

const MEDICATION_COLUMNS: DataTableColumn[] = [
  { key: "name", label: "Name" },
  { key: "dose", label: "Dose" },
  { key: "frequency", label: "Frequency" },
  { key: "prescribingProvider", label: "Prescribing Provider" },
  { key: "collaborationSchedule", label: "Collaboration Schedule With Physician" },
  { key: "purpose", label: "Purpose" },
  { key: "actions", label: "" },
];

const EMPTY_VALUES: MedicationsFormValues = {
  medicationAdherence: "",
  coordinationOfCareNotes: "",
};

export const Medications: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { data: clientData, refetch: refetchClient } = useGetClientById(clientIdNum);
  const { updateClient: updateClientMutation } = useUpdateClient({
    onSuccess: () => {
      refetchClient();
      setEditMode(false);
    },
  });

  const { medications, refetchMedications } = useGetMedications(clientIdNum);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const { createMedicationMutation, isCreateMedicationLoading } = useCreateMedication({
    onSuccess: () => { refetchMedications(); closeModal(); },
  });

  const { updateMedicationMutation, isUpdateMedicationLoading } = useUpdateMedication({
    onSuccess: () => { refetchMedications(); closeModal(); },
  });

  const { deleteMedicationMutation } = useDeleteMedication({
    onSuccess: () => { refetchMedications(); },
  });

  const [editMode, setEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataTableRow | null>(null);
  const [formValues, setFormValues] = useState<MedicationsFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<MedicationsFormValues>(EMPTY_VALUES);

  useEffect(() => {
    const client = clientData?.data;
    if (client) {
      const mapped: MedicationsFormValues = {
        medicationAdherence: client.medicationAdherence || "",
        coordinationOfCareNotes: client.coordinationCareNotes || "",
      };
      setFormValues(mapped);
      setInitialValues(mapped);
    }
  }, [clientData?.data]);

  const handleChange =
    (field: keyof MedicationsFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientId) return;
    updateClientMutation({
      clientId,
      data: {
        medicationAdherence: formValues.medicationAdherence || null,
        coordinationCareNotes: formValues.coordinationOfCareNotes || null,
      },
    });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const handleModalSave = (values: MedicationModalSaveValues) => {
    if (!clientIdNum) return;
    const payload = {
      name: values.name || null,
      doseValue: values.dosageAmount || null,
      doseUnit: values.dosageUnit ? values.dosageUnit.toUpperCase() : null,
      purpose: values.purpose || null,
      frequencyValue: values.frequencyAmount ? Number(values.frequencyAmount) : null,
      frequencyInterval: values.frequencyInterval ? values.frequencyInterval.toUpperCase() : null,
      prescribingDoctor: values.prescribingDoctor || null,
      collaborationScheduleInterval: values.collaborationScheduleInterval ? values.collaborationScheduleInterval.toUpperCase() : null,
      collaborationScheduleValue: values.collaborationScheduleValue ? Number(values.collaborationScheduleValue) : null,
    };

    if (selectedRow) {
      updateMedicationMutation({ id: Number(selectedRow.id), payload });
    } else {
      createMedicationMutation({ clientId: clientIdNum, payload });
    }
  };

  const saveDisabled = JSON.stringify(formValues) === JSON.stringify(initialValues);

  const rows: DataTableRow[] = medications.map((item: any) => ({
    id: String(item.id),
    name: item.name || "-",
    dose: item.doseValue && item.doseUnit ? `${item.doseValue} ${item.doseUnit.toLowerCase()}` : item.doseValue || "-",
    purpose: item.purpose || "-",
    frequency: item.frequencyValue && item.frequencyInterval
      ? `${item.frequencyValue} per ${item.frequencyInterval.toLowerCase()}`
      : "-",
    prescribingProvider: item.prescribingDoctor || "-",
    collaborationSchedule: item.collaborationScheduleValue && item.collaborationScheduleInterval
      ? `${item.collaborationScheduleValue} per ${item.collaborationScheduleInterval.toLowerCase()}`
      : item.collaborationScheduleInterval
        ? item.collaborationScheduleInterval.toLowerCase()
        : "-",
    _name: item.name || "",
    _doseValue: item.doseValue || "",
    _doseUnit: item.doseUnit ? item.doseUnit.toLowerCase() : "ml",
    _purpose: item.purpose || "",
    _frequencyValue: item.frequencyValue != null ? String(item.frequencyValue) : "",
    _frequencyInterval: item.frequencyInterval ? item.frequencyInterval.toLowerCase() : "day",
    _prescribingDoctor: item.prescribingDoctor || "",
    _collaborationScheduleInterval: item.collaborationScheduleInterval ? item.collaborationScheduleInterval.toLowerCase() : "month",
    _collaborationScheduleValue: item.collaborationScheduleValue != null ? String(item.collaborationScheduleValue) : "",
  }));

  return (
    <SectionWrapper
      title="Medications"
      description="Past and Current Client Medications"
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}

      <DataTable
        columns={MEDICATION_COLUMNS}
        rows={rows}
        ariaLabel="Medications"
        emptyMessage="No medication records"
        onAddNew={() => { setSelectedRow(null); setModalOpen(true); }}
        sx={{ mt: 7 }}
        renderCell={(col, row) => {
          if (col.key === "actions") {
            return (
              <RowOptions
                onEdit={() => { setSelectedRow(row); setModalOpen(true); }}
                onDelete={() => deleteMedicationMutation(Number(row.id))}
              />
            );
          }
          return row[col.key] || "-";
        }}
      />

      <MedicationsModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleModalSave}
        clientId={clientId}
        isSaving={isCreateMedicationLoading || isUpdateMedicationLoading}
        initialValues={selectedRow ? {
          name: selectedRow._name as string,
          dosageAmount: selectedRow._doseValue as string,
          dosageUnit: selectedRow._doseUnit as string,
          purpose: selectedRow._purpose as string,
          frequencyAmount: selectedRow._frequencyValue as string,
          frequencyInterval: selectedRow._frequencyInterval as string,
          prescribingDoctor: selectedRow._prescribingDoctor as string,
          collaborationScheduleInterval: selectedRow._collaborationScheduleInterval as string,
          collaborationScheduleValue: selectedRow._collaborationScheduleValue as string,
        } : undefined}
      />
    </SectionWrapper>
  );
};

export default Medications;
